import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import { GtApiService } from '../../services/gtApi';
import {
  PlanogramComplianceResponse,
  ProductRecognitionResponse,
  Task,
  TaskExtended,
  TasksSearchParams,
} from '../../types';
import { Audit } from './components/Audit';

const AT_MAIN_URL = process.env.REACT_APP_AT_MAIN_URL!;

export const TaskAuditPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const [searchParams] = useSearchParams();
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  // Extract URL params
  const imageId = searchParams.get('image_id');
  const storeId = searchParams.get('store_id');
  const chainId = searchParams.get('chain_id');
  const createdAt = searchParams.get('created_at');

  // State for fetched data
  const [productRecognitionData, setProductRecognitionData] =
    useState<ProductRecognitionResponse | null>(null);
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);
  const [allTasks, setAllTasks] = useState<TaskExtended[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingInference, setIsSavingInference] = useState(false);
  const [savedGtSampleId, setSavedGtSampleId] = useState<string | null>(null);

  const handleSaveInference = useCallback(async () => {
    if (!productRecognitionData?.inferenceId) {
      showErrorToast('No inference data to save');
      return;
    }
    try {
      setIsSavingInference(true);
      const result = await GtApiService.createGtSampleFromInference(token, {
        inference_id: productRecognitionData.inferenceId,
      });
      setSavedGtSampleId(result.gt_sample_id);
      showSuccessToast(`Inference saved (ID: ${result.gt_sample_id})`);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error saving inference');
    } finally {
      setIsSavingInference(false);
    }
  }, [productRecognitionData?.inferenceId, token, showErrorToast, showSuccessToast]);

  useEffect(() => {
    setSavedGtSampleId(null);
  }, [productRecognitionData?.inferenceId]);

  // TASK MODE: Fetch task detail
  useEffect(() => {
    const fetchTaskDetail = async () => {
      if (!taskId || !token) return;
      setIsLoading(true);
      try {
        const data = await ApiService.fetchTaskDetail(token, parseInt(taskId, 10));
        setProductRecognitionData(data);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Failed to fetch task detail');
      } finally {
        setIsLoading(false);
      }
    };
    fetchTaskDetail();
  }, [taskId, token, showErrorToast]);

  // TASK MODE: Fetch planogram compliance
  useEffect(() => {
    const fetchPlanogramCompliance = async () => {
      if (!taskId || !token) return;
      try {
        const data = await ApiService.fetchTaskPlanogramCompliance(token, parseInt(taskId, 10));
        setPlanogramComplianceData(data);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Failed to fetch planogram compliance');
      }
    };
    fetchPlanogramCompliance();
  }, [taskId, token, showErrorToast]);

  // Fetch tasks list
  useEffect(() => {
    const fetchTasks = async () => {
      if (!token || !chainId || !imageId) {
        return;
      }

      try {
        const apiParams: TasksSearchParams = {
          chainId: parseInt(chainId),
          imageId,
          ...(storeId && {
            storeId: parseInt(storeId),
          }),
          ...(createdAt && {
            taskDate: createdAt.split('T')[0],
          }),
        };

        const data: Task[] = await ApiService.searchTasks(token, apiParams);
        const extendedTasks: TaskExtended[] = data.map((task) => {
          return {
            ...task,
            atUrl: `${AT_MAIN_URL}/pog-compliance/${task.taskId}?storeName=${task.storeName}`,
          };
        });

        setAllTasks(extendedTasks);
      } catch (err) {
        // Silently fail for task list
      }
    };

    fetchTasks();
  }, [chainId, createdAt, storeId, token, imageId]);

  return (
    <Audit
      productRecognitionData={productRecognitionData}
      planogramComplianceData={planogramComplianceData}
      allTasks={allTasks}
      isLoading={isLoading}
      imageId={imageId}
      onProductRecognitionDataChange={setProductRecognitionData}
      onPlanogramComplianceDataChange={setPlanogramComplianceData}
      onAllTasksChange={setAllTasks}
      onSaveInference={handleSaveInference}
      savedGtSampleId={savedGtSampleId}
      isSavingInference={isSavingInference}
    />
  );
};

export default TaskAuditPage;
